/**
 * SharedProgressView — Iter 74.
 * PUBLIC read-only view of a patient's progress, accessed by a tokenised URL.
 * No auth required. Shows pain reduction, layered body chart and care timeline.
 * The backend strips PHI before returning: first name only, no email/DOB,
 * no clinical-note text.
 *
 * URL: /share/progress/:token
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Sparkles, Activity, Calendar, FileText, HeartHandshake, Layers, ShieldCheck, AlertCircle } from 'lucide-react';

const DISCIPLINE_HEX = {
  physiotherapy:   '#003D80',
  osteopathy:      '#6A1B9A',
  chiropractic:    '#1B5E20',
  massage_therapy: '#FFB000',
  fitness:         '#B71C1C',
};

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

export default function SharedProgressView() {
  const { token } = useParams();
  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/share/progress/view/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${res.status}`);
        }
        setPayload(await res.json());
      } catch (e) {
        setErr(e.message || 'Could not load this share link.');
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center p-8 text-fg-muted">Loading your progress…</div>;
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-bg-secondary">
        <div className="surface border border-soft rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-[#FFB000] mx-auto mb-3"/>
          <h1 className="text-xl font-heading font-bold mb-2">Link unavailable</h1>
          <p className="text-sm text-fg-muted" data-testid="share-public-err">{err}</p>
        </div>
      </div>
    );
  }
  if (!payload) return null;

  const { patient_first_name, clinic_name, clinic_logo_url, summary, snapshots, care_timeline, expires_at } = payload;
  const changeNice = summary?.pain_change != null
    ? (summary.pain_change > 0 ? `+${summary.pain_change}` : `${summary.pain_change}`)
    : null;

  return (
    <div className="min-h-screen bg-bg-secondary py-8 px-4" data-testid="share-public">
      <div className="max-w-3xl mx-auto">
        {/* Clinic header */}
        <div className="flex items-center gap-3 mb-6">
          {clinic_logo_url && <img src={clinic_logo_url} alt="" className="w-10 h-10 rounded-md object-contain bg-white border border-soft p-1"/>}
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-fg-muted">Progress report</div>
            <div className="font-heading font-bold text-lg">{clinic_name}</div>
          </div>
          <ShieldCheck className="w-5 h-5 text-brand" title="PHI-scrubbed read-only view"/>
        </div>

        <div className="surface border border-soft rounded-2xl p-6 mb-4">
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">
            {patient_first_name}, here's your progress.
          </h1>
          <p className="text-sm text-fg-muted mt-2">
            We've captured <strong data-testid="share-session-count">{summary.session_count || 0}</strong> session{summary.session_count === 1 ? '' : 's'} so far.
            {summary.first_pain != null && summary.latest_pain != null && (
              <> Pain went from <strong>{summary.first_pain}/10</strong> to <strong data-testid="share-latest-pain">{summary.latest_pain}/10</strong>
                {changeNice && <> (<span className={summary.pain_change <= 0 ? 'text-[#1B5E20] font-semibold' : 'text-[#B71C1C] font-semibold'}>{changeNice}</span>).</>}</>
            )}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Layered body chart */}
          <div className="surface border border-soft rounded-2xl p-5">
            <h2 className="font-heading font-semibold mb-3 flex items-center gap-2">
              <Layers className="w-4 h-4 text-brand"/> Where it hurts (over time)
            </h2>
            <PublicBodyChart snapshots={snapshots}/>
          </div>

          {/* Pain trend */}
          <div className="surface border border-soft rounded-2xl p-5">
            <h2 className="font-heading font-semibold mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-brand"/> Overall pain trend
            </h2>
            <PublicTrend snapshots={snapshots}/>
          </div>
        </div>

        {/* Care timeline */}
        <div className="surface border border-soft rounded-2xl p-5 mt-4">
          <h2 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-brand"/> What we did together
          </h2>
          {care_timeline.length === 0 ? (
            <p className="text-sm text-fg-muted py-4 text-center" data-testid="share-tl-empty">Nothing to show yet.</p>
          ) : (
            <div className="space-y-2" data-testid="share-tl">
              {care_timeline.map((it, idx) => (
                <PublicTLRow key={idx} item={it}/>
              ))}
            </div>
          )}
        </div>

        <div className="text-center text-[10px] uppercase tracking-wider text-fg-muted mt-6">
          <Sparkles className="w-3 h-3 inline mr-1"/> Powered by Physiogenic
          {expires_at && <> · Link expires {(expires_at || '').slice(0, 10)}</>}
        </div>
      </div>
    </div>
  );
}

// ---- visuals ----
function PublicBodyChart({ snapshots }) {
  const sorted = [...snapshots].sort((a, b) => (a.session_date || '').localeCompare(b.session_date || ''));
  const markers = [];
  sorted.forEach((snap, idx) => {
    const opacity = 0.4 + ((idx + 1) / Math.max(1, sorted.length)) * 0.6;
    (snap.pain_points || []).forEach((p) => {
      if ((p.view || 'anterior') !== 'anterior') return;
      markers.push({
        x: p.x, y: p.y, intensity: p.intensity || 0,
        discipline: snap.discipline || 'physiotherapy',
        opacity, sessionIdx: idx + 1,
      });
    });
  });
  return (
    <div className="relative aspect-[1/2] max-w-[240px] mx-auto bg-bg-secondary rounded-md border border-soft" data-testid="share-bodychart">
      <svg viewBox="0 0 100 200" className="absolute inset-0 w-full h-full" aria-hidden="true">
        <circle cx="50" cy="14" r="9" fill="#E5E2DA"/>
        <rect x="36" y="24" width="28" height="50" rx="6" fill="#E5E2DA"/>
        <rect x="22" y="26" width="10" height="42" rx="4" fill="#E5E2DA"/>
        <rect x="68" y="26" width="10" height="42" rx="4" fill="#E5E2DA"/>
        <rect x="20" y="68" width="10" height="36" rx="4" fill="#E5E2DA"/>
        <rect x="70" y="68" width="10" height="36" rx="4" fill="#E5E2DA"/>
        <rect x="36" y="74" width="28" height="22" rx="6" fill="#E5E2DA"/>
        <rect x="38" y="96" width="11" height="50" rx="4" fill="#E5E2DA"/>
        <rect x="51" y="96" width="11" height="50" rx="4" fill="#E5E2DA"/>
        <rect x="38" y="146" width="11" height="46" rx="4" fill="#E5E2DA"/>
        <rect x="51" y="146" width="11" height="46" rx="4" fill="#E5E2DA"/>
      </svg>
      {markers.map((m, i) => {
        const r = 6 + Math.min(m.intensity, 10) * 0.5;
        const colour = DISCIPLINE_HEX[m.discipline] || '#003D80';
        return (
          <span key={i}
                className="absolute rounded-full border-2 border-white shadow-sm"
                title={`Session ${m.sessionIdx} — pain ${m.intensity}/10`}
                style={{
                  left: `calc(${m.x * 100}% - ${r}px)`,
                  top: `calc(${m.y * 100}% - ${r}px)`,
                  width: r * 2, height: r * 2,
                  background: colour, opacity: m.opacity,
                }}/>
        );
      })}
      {markers.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-[11px] text-fg-muted">
          No markers captured yet.
        </div>
      )}
    </div>
  );
}

function PublicTrend({ snapshots }) {
  const data = [...snapshots]
    .sort((a, b) => (a.session_date || '').localeCompare(b.session_date || ''))
    .map((s) => ({
      date: (s.session_date || '').slice(0, 10),
      pain: s.overall_pain ?? 0,
      discipline: s.discipline || 'physiotherapy',
    }));
  if (data.length === 0) return <p className="text-sm text-fg-muted py-6 text-center">No pain readings yet.</p>;
  const maxX = Math.max(1, data.length - 1);
  const path = data.map((d, i) => {
    const x = (i / maxX) * 100;
    const y = 100 - (d.pain / 10) * 100;
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');
  return (
    <div data-testid="share-trend">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full aspect-[5/2] bg-bg-secondary rounded-md">
        {[0, 25, 50, 75, 100].map((y) => <line key={y} x1="0" x2="100" y1={y} y2={y} stroke="#E5E2DA" strokeWidth="0.3"/>)}
        <path d={path} stroke="#003D80" strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke"/>
        {data.map((d, i) => {
          const x = (i / maxX) * 100;
          const y = 100 - (d.pain / 10) * 100;
          return <circle key={i} cx={x} cy={y} r="1.5" fill={DISCIPLINE_HEX[d.discipline] || '#003D80'} stroke="white" strokeWidth="0.3" vectorEffect="non-scaling-stroke"/>;
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-fg-muted mt-1 px-1">
        <span>{data[0].date}</span>
        {data.length > 1 && <span>{data[data.length - 1].date}</span>}
      </div>
    </div>
  );
}

function PublicTLRow({ item }) {
  const Icon = { appointment: Calendar, note: FileText, massage_session: HeartHandshake }[item.kind] || Activity;
  const colour = DISCIPLINE_HEX[item.discipline] || '#003D80';
  return (
    <div className="flex items-start gap-3 p-3 rounded-md border border-soft">
      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
           style={{ background: `${colour}1A`, color: colour }}>
        <Icon className="w-3.5 h-3.5"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{item.title}</div>
        <div className="text-[11px] text-fg-muted capitalize">{(item.discipline || '').replace('_', ' ')}</div>
      </div>
      <div className="text-[11px] text-fg-muted whitespace-nowrap">{(item.date || '').slice(0, 10)}</div>
    </div>
  );
}
